import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { getPasswordForgot } from "#app/_api/bol-authentication-service/get-password-forgot";
import { DisclosureText } from "#app/_ui/components/disclosures/DisclosureText";
import { Tel } from "#app/_ui/components/media/Tel";
import { hoursOnlineSupport, telOnlineSupport } from "#app/lib/company";
import { logError } from "#app/lib/logger";
import { userIdSchema } from "#app/lib/schemas/field-schemas";
import { toUsaTelHref } from "#app/lib/telephone";
import { Alert, AlertContent, AlertDescription, AlertTitle } from "#ui/alert";
import { Button } from "#ui/button";
import { Card, CardContent, type CardRootProps } from "#ui/card";
import { Field } from "#ui/field";
import { FieldLabel } from "#ui/field-label";
import { FieldMessage } from "#ui/field-message";
import { Link } from "#ui/link";
import { TextInput } from "#ui/text-input";
import { H1, P } from "#ui/typography";

const ForgotPasswordFormSchema = z.object({
  userId: userIdSchema,
});

type ForgotPasswordFormValues = z.infer<typeof ForgotPasswordFormSchema>;

export interface ForgotPasswordIdleProps extends CardRootProps {
  onChangeStatus: (value: "error" | "success" | "pending" | "idle") => void;
}

export function ForgotPasswordIdle({
  onChangeStatus,
  ...props
}: ForgotPasswordIdleProps) {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      userId: "",
    },
    resolver: zodResolver(ForgotPasswordFormSchema),
    mode: "onTouched",
  });

  const { mutate, isPending } = useMutation({
    mutationFn: getPasswordForgot,
    onSuccess: () => {
      onChangeStatus("success");
    },
    onError: (error) => {
      logError(error);
      onChangeStatus("error");
    },
  });

  return (
    <div className="grid w-full max-w-850px gap-30px">
      <Card {...props}>
        <CardContent>
          <div className="grid gap-30px md:grid-cols-2">
            <div>
              <form
                id="forgot-password-form"
                onSubmit={handleSubmit((data) => {
                  mutate({ loginId: data.userId });
                })}
                className="flex flex-col gap-10px md:min-h-280px md:justify-between md:gap-20px"
              >
                <div className="space-y-14px max-md:mb-10px">
                  <H1 size="h6">Forgot Password</H1>
                  <P size="sm" className="text-pretty">
                    To reset your password, please confirm your account details.
                  </P>
                </div>
                <Field>
                  <FieldLabel htmlFor="userId">User ID</FieldLabel>
                  <TextInput
                    {...register("userId")}
                    id="userId"
                    placeholder="Enter your User ID"
                    autoComplete="off"
                    aria-invalid={errors.userId?.message ? "true" : undefined}
                    aria-describedby={
                      errors.userId?.message ? "userId-error" : undefined
                    }
                  />
                  <FieldMessage id="userId-error" intent="error">
                    {errors.userId?.message}
                  </FieldMessage>
                </Field>
                <div>
                  <Button
                    type="submit"
                    pending={isPending}
                    disabled={isPending}
                    className="width-full md:w-auto"
                  >
                    Continue
                  </Button>
                </div>
              </form>
            </div>

            <div>
              <Alert intent="help">
                <AlertContent>
                  <AlertTitle>Need help?</AlertTitle>
                  <AlertDescription>
                    <Link href="/tech-support" underline>
                      Contact Support
                    </Link>{" "}
                    or call{" "}
                    <Tel href={telOnlineSupport} hrefFn={toUsaTelHref}>
                      {telOnlineSupport}
                    </Tel>
                    , {hoursOnlineSupport}.
                  </AlertDescription>
                </AlertContent>
              </Alert>
            </div>
          </div>
        </CardContent>
      </Card>

      <DisclosureText id="disclosure">
        <p className="text-pretty">
          <strong>Disclosure:</strong>
          <br />
          Client&rsquo;s new password will be sent to Client via e-mail
          transmission. Robert W. Baird & Co. Incorporated (&ldquo;Baird&rdquo;)
          will send Client a new password to the e-mail address on record at
          Baird. Client, by utilizing Baird Online, and changing Client&rsquo;s
          password online, acknowledges that it is solely Client&rsquo;s
          responsibility to provide Baird with the e-mail address to which
          Client&rsquo;s new password will be delivered and to provide timely
          updates to Baird should Client&rsquo;s e-mail address change. Baird
          disclaims any liability arising from the electronic transmission of
          confidential information to Client, including but not limited to,
          e-mail transmission. Transmission by electronic means, including
          e-mail, cannot be guaranteed to be secure, timely or error-free.
          Client acknowledges and accepts that it is solely Client&rsquo;s
          responsibility to take all necessary steps to maintain the
          confidentiality of the password upon electronic receipt, and that if
          Client elects to disclose the new password to third parties, the
          Client accepts responsibility for all consequences, regardless of
          foreseeability, that result from such action.
        </p>
      </DisclosureText>
    </div>
  );
}
